import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  FormHelperText,
  Link,
  MenuItem,
  Stack,
} from '@mui/material';
import Grid from '@mui/system/Unstable_Grid/Grid';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { readerRegister } from 'src/api/public/reader';
import { useGetPublicReaderAccountFees } from 'src/api/public/readerAccountFee';
import { BlockItem, RHFSelect, RHFTextField, RHFUpload } from 'src/components/hook-form';
import FormProvider from 'src/components/hook-form/form-provider';
import { DOCUMENT_HOST_API, MAX_FILE_SIZE } from 'src/config-global';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { IReaderAccountFee } from 'src/types/DTO/readerAccountFee/IReaderAccountFee';
import { REGEXR } from 'src/utils/constant';
import { fCurrency } from 'src/utils/format-number';
import * as Yup from 'yup';
import { convertToFormData, handleError } from './formUtil';

export default function DangKyThe() {
  const { data: readerAccountFee } = useGetPublicReaderAccountFees({
    unlimited: 1,
  });
  const { replace } = useRouter();
  const Schema = Yup.object().shape({
    name: Yup.string().required('Bạn chưa nhập họ và tên !'),
    cardId: Yup.string().required('Bạn chưa nhập số CMND/CCCD !').max(12, 'Vượt quá 12 ký tự'),
    email: Yup.string()
      .max(255, 'Tối đa 255 kí tự')
      .email('Email không hợp lệ')
      .required('Bạn chưa nhập địa chỉ mail'),
    phone: Yup.string()
      .nullable()
      .max(10, 'Tối đa 10 kí tự')
      .matches(REGEXR.phone, 'Số điện thoại không hợp lệ')
      .required('Bạn chưa nhập số điện thoại !'),
    photo: Yup.mixed().nullable().required('Bạn chưa chọn hình 3x4 !'),
    cardFront: Yup.mixed().nullable().required('Bạn chưa chọn hình ảnh mặt trước CMND/CCCD !'),
    cardBack: Yup.mixed().nullable().required('Bạn chưa chọn hình ảnh mặt sau CMND/CCCD !'),
    registerForm: Yup.mixed().nullable().required('Bạn chưa chọn đơn đăng ký !'),
    readerAccountFee: Yup.string().required('Bạn chưa chọn thời gian sử dụng !'),
  });
  const defaultValues = useMemo(
    () => ({
      name: '',
      cardId: '',
      email: '',
      phone: '',
      photo: null as any,
      cardFront: null as any,
      cardBack: null as any,
      registerForm: null as any,
      readerAccountFee: '',
    }),
    []
  );
  const methods = useForm({
    resolver: yupResolver(Schema),
    defaultValues,
  });

  const {
    reset,
    setValue,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = handleSubmit(async (data: any) => {
    try {
      await readerRegister(convertToFormData(data));
      enqueueSnackbar('Đăng ký mở thẻ thành công !', { variant: 'success' });
      replace(paths.guest.tra_cuu);
      reset();
    } catch (error) {
      handleError(error, setError);
    }
  });
  const handleDropSingleFile = useCallback(
    (acceptedFiles: File[], name: 'photo' | 'cardFront' | 'cardBack' | 'registerForm') => {
      const file = acceptedFiles[0];

      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (newFile) {
        setValue(name, newFile, { shouldValidate: true });
      }
    },
    [setValue]
  );
  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Card sx={{ maxWidth: 700, p: 2, mx: 'auto' }}>
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              Đăng ký thẻ bạn đọc
              <Link
                href={`${DOCUMENT_HOST_API}/documents/samples/Don_dk_lam_the_9-2023-ngoai_truong.docx`}
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
                ml={0.5}
                sx={{ typography: 'subtitle2' }}
              >
                Tải đơn đăng ký
              </Link>
            </Stack>
          }
          mb={1}
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <BlockItem label="Họ và tên : " required>
                <RHFTextField name="name" size="small" placeholder="Nguyễn Văn A..." />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Số CMND/CCCD : " required>
                <RHFTextField name="cardId" size="small" placeholder="Số CMND/CCCD..." />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Email : " required>
                <RHFTextField name="email" size="small" placeholder="mail@mail.com..." />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Số điện thoại : " required>
                <RHFTextField name="phone" size="small" placeholder="0123456789..." />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Ảnh 3x4 : " required>
                <RHFUpload
                  name="photo"
                  maxSize={MAX_FILE_SIZE}
                  onDrop={(files) => handleDropSingleFile(files, 'photo')}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                  }}
                  onDelete={() => setValue('photo', null, { shouldValidate: true })}
                  filePlaceholder={
                    <>
                      Chỉ chấp nhận file <strong>PNG</strong> và <strong>JPEG</strong>
                    </>
                  }
                />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Ảnh mặt trước CMND/CCCD : " required>
                <RHFUpload
                  name="cardFront"
                  maxSize={MAX_FILE_SIZE}
                  onDrop={(files) => handleDropSingleFile(files, 'cardFront')}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                  }}
                  onDelete={() => setValue('cardFront', null, { shouldValidate: true })}
                  filePlaceholder={
                    <>
                      Chỉ chấp nhận file <strong>PNG</strong> và <strong>JPEG</strong>
                    </>
                  }
                />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Ảnh mặt sau CMND/CCCD : " required>
                <RHFUpload
                  name="cardBack"
                  maxSize={MAX_FILE_SIZE}
                  onDrop={(files) => handleDropSingleFile(files, 'cardBack')}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                  }}
                  onDelete={() => setValue('cardBack', null, { shouldValidate: true })}
                  filePlaceholder={
                    <>
                      Chỉ chấp nhận file <strong>PNG</strong> và <strong>JPEG</strong>
                    </>
                  }
                />
              </BlockItem>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Đơn đăng ký làm thẻ : " required>
                <RHFUpload
                  name="registerForm"
                  maxSize={MAX_FILE_SIZE}
                  onDrop={(files) => handleDropSingleFile(files, 'registerForm')}
                  accept={{
                    'image/jpeg': [],
                    'image/png': [],
                  }}
                  onDelete={() => setValue('registerForm', null, { shouldValidate: true })}
                  filePlaceholder={
                    <>
                      Chỉ chấp nhận file <strong>PNG</strong> và <strong>JPEG</strong>
                    </>
                  }
                />
              </BlockItem>
              <FormHelperText sx={{ typography: 'subtitle2' }}>
                Tải đơn đăng ký
                <Link
                  href={`${DOCUMENT_HOST_API}/samples/Don_dk_lam_the_9-2023-ngoai_truong.docx`}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="always"
                  ml={0.5}
                >
                  tại đây!
                </Link>
              </FormHelperText>
            </Grid>
            <Grid xs={12}>
              <BlockItem label="Thời gian sử dụng : " required>
                <RHFSelect name="readerAccountFee" size="small">
                  {readerAccountFee.data.list.map((option: IReaderAccountFee) => (
                    <MenuItem key={option.id} value={option.id}>
                      {`${option.duration} tháng (${fCurrency(option.fee)})`}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </BlockItem>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions>
          <LoadingButton
            type="submit"
            loading={isSubmitting}
            color="primary"
            variant="contained"
            sx={{ ml: 'auto', minWidth: 120 }}
          >
            Đăng ký
          </LoadingButton>
        </CardActions>
      </Card>
    </FormProvider>
  );
}
